import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTelegram } from '../contexts/TelegramContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { LoginButton, TelegramAuthData } from '@telegram-auth/react';
import { getTelegramLoginBotUsername } from '../utils/config';
import { SeoHead } from '../components/SeoHead';
import { getSeoForPath } from '../config/seoPages';

// 主题切换按钮组件
const ThemeToggleButton: React.FC = () => {
  const { toggleTheme, resolvedTheme } = useTheme();
  
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shadow-sm"
      title="切换主题"
    >
      {resolvedTheme === 'dark' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, telegramWidgetLogin, user } = useAuth();
  const { isInTelegram, user: telegramUser } = useTelegram();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const seoMeta = getSeoForPath(pathname);
  const telegramBotUsername = getTelegramLoginBotUsername();
  const telegramMiniAppUrl = telegramBotUsername
    ? `https://t.me/${telegramBotUsername}/?startapp`
    : 'https://t.me/?startapp';

  // 检查用户是否已登录，如果已登录则自动跳转到dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleTelegramAuth = async (telegramAuthUser: TelegramAuthData) => {
    setTelegramLoading(true);
    setError('');
    try {
      await telegramWidgetLogin(telegramAuthUser);
      navigate('/dashboard');
    } catch (err: any) {
      setError(
        err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat()[0] as string
          : err.response?.data?.message || 'Telegram 登录失败，请稍后重试'
      );
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {

      // 从错误响应对象中提取第一个错误信息
      // 1. Object.values() 获取 errors 对象中所有的值
      // 2. flat() 将嵌套数组展平为一维数组
      // 3. [0] 获取第一个错误消息
      setError(
        err.response?.data?.errors
          ? Object.values(err.response.data.errors).flat()[0]
          : err.response?.data?.message || '登录失败，请检查邮箱和密码'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SeoHead meta={seoMeta} />
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors">
      {/* 主题切换按钮 */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggleButton />
      </div>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-white transition-colors">
            登录到您的账户
          </h2>
          {isInTelegram && telegramUser && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    检测到您正在使用 Telegram，欢迎 {telegramUser.first_name}！
                    <br />
                    如果您已有账户，系统将自动为您登录。
                  </p>
                </div>
              </div>
            </div>
          )}
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400 transition-colors">
            或者{' '}
            <Link
              to="/register"
              className="font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              创建新账户
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md text-sm transition-colors">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                邮箱地址
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="请输入邮箱地址"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                密码
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="请输入密码"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || telegramLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-900 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </div>

          {!isInTelegram && (
            <>
              <div>
                <button
                  type="button"
                  onClick={() => window.open(telegramMiniAppUrl, '_blank', 'noopener,noreferrer')}
                  className="w-full flex justify-center py-2 px-4 border border-cyan-300 dark:border-cyan-700 text-sm font-medium rounded-md text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 hover:bg-cyan-100 dark:hover:bg-cyan-900/35 transition-colors"
                >
                  在 Telegram 中打开（推荐）
                </button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-700" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">备用方式</span>
                </div>
              </div>

              {telegramBotUsername ? (
                <div className="p-3 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/30">
                 
                  <div className="flex justify-center opacity-90">
                    <LoginButton
                      botUsername={telegramBotUsername}
                      buttonSize="medium"
                      lang="zh"
                      showAvatar={false}
                      requestAccess={null}
                      widgetVersion={23}
                      onAuthCallback={handleTelegramAuth}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                  未配置 Telegram 登录机器人用户名，请在 `public/config.js` 中设置 `TELEGRAM_LOGIN_BOT_USERNAME`。
                </p>
              )}

              {telegramLoading && (
                <p className="text-center text-sm text-primary-600 dark:text-primary-400">
                  正在通过 Telegram 登录...
                </p>
              )}
            </>
          )}
        </form>
      </div>
    </div>
    </>
  );
};